import React, { useEffect, useRef } from 'react';
import { FiX, FiZap, FiSearch, FiArrowUp, FiCornerDownLeft, FiPlus, FiEdit, FiCopy, FiHelpCircle, FiInfo } from 'react-icons/fi';
import { MdFormatBold, MdFormatItalic, MdFormatUnderlined } from 'react-icons/md';
import KeyboardShortcutBadge from '../shared/KeyboardShortcutBadge';
import { getSearchShortcut, getModifierKey, getShortcutsModalShortcut, getNewMessageShortcut } from '../../utils/keyboardUtils';

/**
 * Modal qui affiche tous les raccourcis clavier disponibles
 */
const KeyboardShortcutsModal = ({ isOpen, onClose }) => {
    const modalRef = useRef(null);

    // Focus management & keyboard accessibility
    useEffect(() => {
        if (!isOpen) return;

        const previouslyFocused = document.activeElement;
        
        // Focus sur le premier élément focusable
        if (modalRef.current) {
            const focusable = modalRef.current.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            
            if (focusable.length > 0) {
                focusable[0].focus();
            }
        }

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onClose();
                return;
            }

            // Focus trap
            if (e.key === 'Tab' && modalRef.current) {
                const focusable = modalRef.current.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                const first = focusable[0];
                const last = focusable[focusable.length - 1];

                if (e.shiftKey) {
                    if (document.activeElement === first) {
                        e.preventDefault();
                        last.focus();
                    }
                } else {
                    if (document.activeElement === last) {
                        e.preventDefault();
                        first.focus();
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            previouslyFocused?.focus();
        };
    }, [isOpen, onClose]);

    // Prevent background scroll
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const shortcuts = [
        {
            category: 'Messages',
            items: [
                { keys: getNewMessageShortcut(), icon: FiPlus, description: 'Créer un nouveau message', available: true },
                { keys: getSearchShortcut(), icon: FiSearch, description: 'Ouvrir la recherche rapide', available: true },
            ]
        },
        {
            category: 'Navigation dans la recherche',
            items: [
                { keys: '↑ ↓', icon: FiArrowUp, description: 'Naviguer entre les résultats', available: true },
                { keys: 'Enter', icon: FiCornerDownLeft, description: 'Ouvrir le message sélectionné', available: true },
                { keys: 'Esc', icon: FiX, description: 'Fermer la recherche / quitter la sélection', available: true },
            ]
        },
        {
            category: 'Éditeur de texte',
            items: [
                { keys: `${getModifierKey()}+B`, icon: MdFormatBold, description: 'Gras', available: true },
                { keys: `${getModifierKey()}+I`, icon: MdFormatItalic, description: 'Italique', available: true },
                { keys: `${getModifierKey()}+U`, icon: MdFormatUnderlined, description: 'Souligné', available: true },
            ]
        },
        {
            category: 'Interface',
            items: [
                { keys: getShortcutsModalShortcut(), icon: FiHelpCircle, description: 'Afficher / masquer les raccourcis', available: true },
            ]
        },
        {
            category: 'À venir',
            items: [
                { keys: `${getModifierKey()}+E`, icon: FiEdit, description: 'Éditer le message sélectionné', available: false },
                { keys: `${getModifierKey()}+D`, icon: FiCopy, description: 'Dupliquer le message', available: false },
            ]
        }
    ];

    return (
        <>
            {/* Overlay */}
            <div 
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
                onClick={onClose}
                aria-hidden="true"
            />
            
            {/* Modal */}
            <div 
                className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="keyboard-shortcuts-title"
            >
                <div 
                    ref={modalRef}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-200 dark:border-gray-700"
                    onClick={(e) => e.stopPropagation()}
                    tabIndex={-1}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20">
                        <div className="flex items-center gap-3">
                            <FiZap className="text-indigo-600 dark:text-indigo-400 text-2xl" />
                            <h2 id="keyboard-shortcuts-title" className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                Raccourcis clavier
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            aria-label="Fermer"
                        >
                            <FiX className="text-xl" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                            Gagnez en productivité avec les raccourcis clavier. Utilisez <strong>Ctrl</strong> sur Windows/Linux ou <strong>⌘</strong> sur Mac et iOS.
                        </p>

                        {shortcuts.map((section, idx) => (
                            <div key={idx} className="mb-6 last:mb-0">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                    {section.category}
                                </h3>
                                <div className="space-y-2">
                                    {section.items.map((item, itemIdx) => {
                                        const IconComponent = item.icon;
                                        return (
                                            <div 
                                                key={itemIdx}
                                                className={`flex items-center justify-between p-3 rounded-lg border ${
                                                    item.available 
                                                        ? 'bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700' 
                                                        : 'bg-gray-50 dark:bg-gray-900/30 border-gray-100 dark:border-gray-800 opacity-60'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    {IconComponent && <IconComponent className="text-xl flex-shrink-0 text-indigo-600 dark:text-indigo-400" />}
                                                    <span className={`text-sm ${
                                                        item.available 
                                                            ? 'text-gray-700 dark:text-gray-300' 
                                                            : 'text-gray-500 dark:text-gray-500'
                                                    }`}>
                                                        {item.description}
                                                    </span>
                                                    {!item.available && (
                                                        <span className="text-xs text-gray-400 dark:text-gray-600 italic ml-auto">
                                                            Prochainement
                                                        </span>
                                                    )}
                                                </div>
                                                <KeyboardShortcutBadge 
                                                    shortcut={item.keys} 
                                                    variant={item.available ? 'primary' : 'default'}
                                                    className="flex-shrink-0 ml-3"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}

                        <div className="mt-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                            <div className="flex items-start gap-2">
                                <FiInfo className="text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-indigo-900 dark:text-indigo-200">
                                    <strong>Astuce :</strong> Les raccourcis sont affichés directement dans l'interface avec des badges pour vous aider à les mémoriser.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default KeyboardShortcutsModal;
